// src/pages/URDList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaChartBar,
} from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { FiRotateCcw } from "react-icons/fi";
import { FaPrint } from "react-icons/fa";
import InvoicePrintPreviewModal from "../components/InvoicePrintPreviewModal";
import Select from "react-select";
import toast from "react-hot-toast";

import ConfirmDialog from "../components/ConfirmDialog";

import {
  useGetUrdsQuery,
  useDeleteUrdMutation,
  useBulkDeleteUrdsMutation,
  useDownloadUrdReceiptPdfMutation,
} from "../toolkitServices/urdApi";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";

import { handleDropDowns } from "../config/dropDownHelpers";
import { fmtDate } from "../utils/date";
import { useSelector } from "react-redux";

/* ------------ Tiny reusable spinner (same vibe as LabelledStock) ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ----------------- helpers ----------------- */
const toTitle = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const money = (n) => {
  const v = Number(n ?? 0);
  return isFinite(v) ? v.toFixed(2) : "0.00";
};

// react-select styling: compact, same as LabelledStockList
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "0.8rem",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#111827",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-1 sm:px-2 py-1.5 font-semibold uppercase tracking-wide";
const dataCell = "px-1 sm:px-2 py-1.5";

/* ------------- skeleton rows ------------- */
const SkeletonRows = ({ cols = 10, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const URDList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // 👇 for focusing customer dropdown when filter modal opens
  const customerSelectRef = useRef(null);

  /* ---------------- header controls ---------------- */
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // client-side sorting (on current page)
  const [sortBy, setSortBy] = useState("urdDate");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  // ✅ modals
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  // filters (draft vs applied)
  const emptyFilter = {
    branchId: "",
    customerId: "",
    from: "",
    to: "",
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyFilter);
  const [draftFilters, setDraftFilters] = useState(emptyFilter);

  // selections (for bulk delete)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // confirm dialog state
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'single' | 'bulk'
    payload: null, // { id, label } or { ids, count }
  });

  /* ---------------- masters ---------------- */
  const { data: branches } = useGetAllBranchesQuery();
  const [customerSearch, setCustomerSearch] = useState("");
  const { data: customersResp, isFetching: loadingCustomers } =
    useGetAllCustomersQuery({
      page: 1,
      limit: 20,
      sortOrder: "desc",
      sortBy: "createdAt",
      search: customerSearch,
    });

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branches?.data }),
    [branches],
  );
  const customerOptions = useMemo(
    () => handleDropDowns({ key: "customer", customers: customersResp?.data }),
    [customersResp],
  );

  /* ---------------- data query ---------------- */
  const { data, isFetching, isLoading, isSuccess, refetch } = useGetUrdsQuery({
    page,
    limit,
    ...appliedFilters,
  });

  const rows = data?.rows || [];
  const totalDocs = data?.total ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalDocs / (Number(data?.limit) || limit)),
  );
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // ✅ Summary objects from API
  const summary = data?.summary || null; // filters wise (all docs)
  const pageSummary = data?.pageSummary || null; // current page totals
  const hasSummary = !!(summary || pageSummary);

  const fmt = (n, d = 3) => Number(n || 0).toFixed(d);
  const fmt0 = (n) => Number(n || 0).toFixed(0);
  const rs = (n) => `₹ ${money(n)}`;

  const summaryCards = useMemo(() => {
    const s = summary || {};
    const p = pageSummary || {};

    return [
      {
        key: "qty",
        label: "Qty",
        allVal: summary ? fmt0(s.qty) : "—",
        pageVal: pageSummary ? fmt0(p.qty) : "—",
      },
      {
        key: "totalWt",
        label: "Total Wt",
        allVal: summary ? fmt(s.totalWt) : "—",
        pageVal: pageSummary ? fmt(p.totalWt) : "—",
      },
      {
        key: "grossWt",
        label: "Gross Wt",
        allVal: summary ? fmt(s.grossWt) : "—",
        pageVal: pageSummary ? fmt(p.grossWt) : "—",
      },
      {
        key: "stoneWt",
        label: "Stone Wt",
        allVal: summary ? fmt(s.stoneWt) : "—",
        pageVal: pageSummary ? fmt(p.stoneWt) : "—",
      },

      // ✅ Diamond Weight (using diamondGram)
      {
        key: "diamondGram",
        label: "Diamond Wt",
        allVal: summary ? fmt(s.diamondGram) : "—",
        pageVal: pageSummary ? fmt(p.diamondGram) : "—",
      },

      {
        key: "netWt",
        label: "Net Wt",
        allVal: summary ? fmt(s.netWt) : "—",
        pageVal: pageSummary ? fmt(p.netWt) : "—",
      },
      {
        key: "fineWt",
        label: "Fine Wt",
        allVal: summary ? fmt(s.fineWt) : "—",
        pageVal: pageSummary ? fmt(p.fineWt) : "—",
      },
    ];
  }, [summary, pageSummary]);

  // allow delete unless explicitly blocked
  const isDeletable = (it) => it?.isActive !== false;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/purchase/urd-gst-list/create-urd");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // ESC close for modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsFilterOpen(false);
        setIsSummaryOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // clear selection when context changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, limit, appliedFilters, sortBy, sortOrder, search, isSuccess]);

  /* 👇 When filter modal opens, focus customer dropdown */
  useEffect(() => {
    if (!isFilterOpen) return;

    const t = setTimeout(() => {
      if (
        customerSelectRef.current &&
        typeof customerSelectRef.current.focus === "function"
      ) {
        customerSelectRef.current.focus();
      }
    }, 80);

    return () => clearTimeout(t);
  }, [isFilterOpen]);

  /* ---------------- filters helpers ---------------- */
  const findOpt = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v)) || null;

  const branchValue = useMemo(
    () => findOpt(branchOptions, draftFilters.branchId),
    [branchOptions, draftFilters.branchId],
  );
  const customerValue = useMemo(
    () => findOpt(customerOptions, draftFilters.customerId),
    [customerOptions, draftFilters.customerId],
  );

  const onBranchChange = (opt) =>
    setDraftFilters((s) => ({ ...s, branchId: opt?.value || "" }));
  const onCustomerChange = (opt) =>
    setDraftFilters((s) => ({ ...s, customerId: opt?.value || "" }));
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((s) => ({ ...s, [name]: value }));
  };

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };
  const resetDraftFilters = () => setDraftFilters(emptyFilter);

  // chips (applied filters + search)
  const labelFromOpts = (opts, v) => findOpt(opts, v)?.label || "";
  const chips = useMemo(() => {
    const list = [];
    if (appliedFilters.branchId)
      list.push({
        key: "branchId",
        label: `Branch: ${labelFromOpts(branchOptions, appliedFilters.branchId)}`,
      });
    if (appliedFilters.customerId)
      list.push({
        key: "customerId",
        label: `Customer: ${labelFromOpts(
          customerOptions,
          appliedFilters.customerId,
        )}`,
      });
    if (appliedFilters.from)
      list.push({ key: "from", label: `From: ${appliedFilters.from}` });
    if (appliedFilters.to)
      list.push({ key: "to", label: `To: ${appliedFilters.to}` });
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [appliedFilters, search, branchOptions, customerOptions]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    else setAppliedFilters((s) => ({ ...s, [key]: "" }));
    setPage(1);
  };
  const clearAllChips = () => {
    setSearch("");
    setAppliedFilters(emptyFilter);
    setPage(1);
  };

  /* ---------------- sorting ---------------- */
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  // client-side search + sort on current page rows
  const filteredSortedRows = useMemo(() => {
    let arr = Array.isArray(rows) ? [...rows] : [];
    const q = (search || "").trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) => {
        const c = r?.customerId;
        const customerText = [
          c?.customerName,
          c?.customerId,
          c?.contact,
          c?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const urdNoText = String(r?.urdNo ?? "").toLowerCase();
        const branchText = (r?.branchId?.branchName || "").toLowerCase();
        return (
          customerText.includes(q) ||
          urdNoText.includes(q) ||
          branchText.includes(q)
        );
      });
    }

    const dir = sortOrder === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const get = (row) => {
        switch (sortBy) {
          case "urdNo":
            return Number(row?.urdNo || 0);
          case "urdDate":
            return new Date(row?.urdDate || 0).getTime();
          case "amount":
            return Number(row?.totals?.amount || 0);
          case "outstanding":
            return Number(row?.totals?.outstanding || 0);
          case "customerName":
            return (row?.customerId?.customerName || "").toLowerCase();
          case "branchName":
            return (row?.branchId?.branchName || "").toLowerCase();
          default:
            return new Date(row?.urdDate || 0).getTime();
        }
      };
      const av = get(a);
      const bv = get(b);
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv) * dir;
      }
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });

    return arr;
  }, [rows, search, sortBy, sortOrder]);

  /* ---------------- selection helpers ---------------- */
  const deletableIdsOnPage = (filteredSortedRows || [])
    .filter(isDeletable)
    .map((r) => String(r._id));

  const allSelectedOnPage =
    deletableIdsOnPage.length > 0 &&
    deletableIdsOnPage.every((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        deletableIdsOnPage.forEach((id) => next.delete(id));
      } else {
        deletableIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // selected docs for chip bar
  const selectedDocs = useMemo(() => {
    const set = selectedIds;
    return filteredSortedRows.filter((r) => set.has(String(r._id)));
  }, [filteredSortedRows, selectedIds]);

  const selectedCount = selectedIds.size;
  const showActions = selectedCount === 0;

  // checkbox + 11 data cols (incl. Settlement) + (optional) action
  const columnCount = showActions ? 13 : 12;

  /* ---------------- mutations ---------------- */
  const [deleteUrd, { isLoading: deletingOne }] = useDeleteUrdMutation();
  const [bulkDeleteUrds, { isLoading: deletingMany }] =
    useBulkDeleteUrdsMutation();
  const [downloadUrdReceiptPdf] = useDownloadUrdReceiptPdfMutation();
  const [urdPreview, setUrdPreview] = useState({
    open: false,
    html: "",
    loading: false,
  });
  // confirm handlers
  const openConfirmSingle = (urd) => {
    if (!isDeletable(urd)) return;
    const cust =
      urd?.customerId?.customerName || urd?.customerId?.customerId || "";
    const label = `#${urd?.urdNo || ""} ${cust ? `• ${cust}` : ""}`;
    setConfirm({
      open: true,
      mode: "single",
      payload: { id: urd._id, label },
    });
  };

  const openConfirmBulk = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setConfirm({
      open: true,
      mode: "bulk",
      payload: { ids, count: ids.length },
    });
  };

  const closeConfirm = () =>
    setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    try {
      if (confirm.mode === "single") {
        const { id, label } = confirm.payload || {};
        await deleteUrd(id).unwrap();
        toast.success(`Deleted ${label}`);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
        refetch();
      } else if (confirm.mode === "bulk") {
        const { ids, count } = confirm.payload || {};
        const res = await bulkDeleteUrds(ids).unwrap();
        const removed = res?.deleted ?? count;
        const skipped = (res?.blocked || []).length;
        toast.success(
          `Deleted ${removed}${skipped ? ` • Skipped ${skipped}` : ""}`,
        );
        setSelectedIds(new Set());
        refetch();
      }
    } catch (e) {
      const msg =
        e?.data?.message || e?.data?.msg || e?.error || "Operation failed";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  /* ---------------- debounce: header changes -> page 1 ---------------- */
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [search, limit, appliedFilters, sortBy, sortOrder]);

  const openReceipt = async (id) => {
    if (!id) return;

    try {
      setUrdPreview({
        open: true,
        html: "",
        loading: true,
      });

      const htmlContent = await downloadUrdReceiptPdf(id).unwrap();

      setUrdPreview({
        open: true,
        html: htmlContent,
        loading: false,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load URD preview.");

      setUrdPreview({
        open: false,
        html: "",
        loading: false,
      });
    }
  };

  const closeUrdPreview = () => {
    setUrdPreview({
      open: false,
      html: "",
      loading: false,
    });
  };

  // Top-bar values: these are the currently applied filters.
  const appliedBranchValue = findOpt(branchOptions, appliedFilters.branchId);
  const appliedCustomerValue = findOpt(customerOptions, appliedFilters.customerId);

  const updateTopFilter = (key, value) => {
    setAppliedFilters((prev) => ({
      ...prev,
      [key]: value || "",
    }));
    setPage(1);
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* Global loader overlay (first load) */}
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading URD entries…</span>
          </div>
        </div>
      )}

      {/* Top header bar – same layout as LabelledStockList */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce]  text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">
            URD List
          </h2>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/purchase/urd-gst-list/create-urd"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-slate-50/80">
        {/* MOBILE */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            type="text"
            placeholder="Search URD / customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
          />

          <button
            onClick={() => setIsSummaryOpen(true)}
            disabled={!hasSummary}
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50 disabled:opacity-50"
            title="Summary"
            aria-label="URD summary"
          >
            <FaChartBar className="text-[11px]" />
          </button>

          <button
            onClick={openFilters}
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50"
            title="Filters"
            aria-label="Filters"
          >
            <FaFilter className="text-[11px]" />
          </button>
        </div>

        {/* TABLET + DESKTOP — always one line */}
        <div className="hidden sm:flex w-full min-w-0 flex-nowrap items-end gap-1.5 lg:gap-2 xl:gap-3">
          {/* Branch */}
          <div className="flex min-w-0 flex-[1_1_110px] flex-col gap-[1px] xl:max-w-[190px]">
            <label className="text-[9px] md:text-[10px] lg:text-[11px] font-medium text-slate-600">
              Branch
            </label>
            <Select
              isClearable
              isSearchable
              options={branchOptions}
              value={appliedBranchValue}
              onChange={(opt) => updateTopFilter("branchId", opt?.value)}
              placeholder="Select..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* Customer */}
          <div className="flex min-w-0 flex-[1.35_1_130px] flex-col gap-[1px] xl:max-w-[250px]">
            <label className="text-[9px] md:text-[10px] lg:text-[11px] font-medium text-slate-600">
              Customer
            </label>
            <Select
              isClearable
              isSearchable
              options={customerOptions}
              value={appliedCustomerValue}
              onChange={(opt) => updateTopFilter("customerId", opt?.value)}
              onInputChange={(input, meta) => {
                if (meta.action === "input-change") {
                  setCustomerSearch(input.trim());
                }
              }}
              isLoading={loadingCustomers}
              placeholder="Select..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* From */}
          <div className="flex min-w-0 flex-[0.8_1_105px] flex-col gap-[1px] xl:max-w-[140px]">
            <label className="text-[9px] md:text-[10px] lg:text-[11px] font-medium text-slate-600">
              From
            </label>
            <input
              type="date"
              value={appliedFilters.from}
              onChange={(e) => updateTopFilter("from", e.target.value)}
              className="h-[34px] w-full min-w-0 rounded-lg border border-slate-200 bg-white px-1.5 lg:px-2.5 text-[9px] lg:text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* To */}
          <div className="flex min-w-0 flex-[0.8_1_105px] flex-col gap-[1px] xl:max-w-[140px]">
            <label className="text-[9px] md:text-[10px] lg:text-[11px] font-medium text-slate-600">
              To
            </label>
            <input
              type="date"
              value={appliedFilters.to}
              onChange={(e) => updateTopFilter("to", e.target.value)}
              className="h-[34px] w-full min-w-0 rounded-lg border border-slate-200 bg-white px-1.5 lg:px-2.5 text-[9px] lg:text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Search */}
          <div className="flex min-w-0 flex-[1.15_1_120px] flex-col gap-[1px]">
            <label className="text-[9px] md:text-[10px] lg:text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="URD no, customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-[34px] w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 lg:px-3 text-[10px] lg:text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Summary */}
          <div className="flex shrink-0 flex-col gap-[1px]">
            <label className="invisible text-[9px] md:text-[10px] lg:text-[11px] font-medium">
              Summary
            </label>
            <button
              onClick={() => setIsSummaryOpen(true)}
              disabled={!hasSummary}
              className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50 disabled:opacity-50 lg:w-auto lg:px-3"
              title="Summary"
              aria-label="URD summary"
            >
              <FaChartBar className="text-[11px]" />
              <span className="hidden lg:ml-1 lg:inline text-[11px]">Summary</span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex shrink-0 flex-col gap-[1px]">
            <label className="invisible text-[9px] md:text-[10px] lg:text-[11px] font-medium">
              Filters
            </label>
            <button
              onClick={openFilters}
              className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50 lg:w-auto lg:px-3"
              title="Advanced filters"
              aria-label="Advanced filters"
            >
              <FaFilter className="text-[11px]" />
              <span className="hidden lg:ml-1 lg:inline text-[11px]">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Selected items bar (like LabelledStock) */}
      {selectedCount > 0 && (
        <div className="px-4 py-2.5 border-b bg-gradient-to-r from-violet-50 via-indigo-50 to-slate-50">
          <div className="flex items-center gap-3">
            {/* Left: count + text */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="h-6 w-6 rounded-full bg-[#6b59c6] text-white flex items-center justify-center text-[11px] shadow-sm">
                {selectedCount}
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[11px] font-semibold text-slate-800">
                  {selectedCount} URD{selectedCount > 1 ? "s" : ""} selected
                </span>
                <span className="text-[10px] text-slate-500">
                  Use bulk delete to remove multiple entries
                </span>
              </div>
            </div>

            {/* Middle: scrollable selected URD chips */}
            <div className="flex-1 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max pr-2">
                {selectedDocs.map((doc) => {
                  const cust =
                    doc?.customerId?.customerName ||
                    doc?.customerId?.customerId ||
                    "";
                  return (
                    <button
                      key={doc._id}
                      type="button"
                      className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-violet-100 text-[11px] text-slate-700 shadow-[0_1px_3px_rgba(15,23,42,0.06)] hover:border-[#6b59c6]/70 hover:bg-violet-50/80 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <span className="font-medium text-[#6b59c6]">
                        #{doc.urdNo || "—"}
                      </span>
                      {cust && (
                        <span className="text-[10px] text-slate-500">
                          · {cust}
                        </span>
                      )}

                      <FaTimesCircle
                        className="ml-1 text-[10px] text-slate-300 group-hover:text-rose-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(String(doc._id));
                            return next;
                          });
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: bulk delete + clear all */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={openConfirmBulk}
                disabled={deletingMany || !selectedCount}
                className="text-[11px] text-rose-600 hover:text-rose-700 bg-rose-50 border border-rose-100 px-3 py-1 rounded-full flex items-center gap-1 disabled:opacity-40"
              >
                {deletingMany ? "Deleting…" : "Delete selected"}
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-[11px] text-slate-600 hover:text-slate-800 bg-white border border-slate-200 px-3 py-1 rounded-full flex items-center gap-1"
              >
                <FaTimesCircle className="text-[11px]" />
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TABLE VIEW ===== */}
      <div
        className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto"
        aria-busy={isFetching}
      >
        <table className="min-w-full text-[10px] sm:text-xs text-left whitespace-nowrap">
          <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[8px] uppercase text-[#4b3d91] sm:text-[9px]">
            <tr>
              {/* Select all checkbox */}
              <th className="w-9 px-1 sm:px-2 py-1.5">
                <input
                  type="checkbox"
                  checked={
                    !!(
                      deletableIdsOnPage.length &&
                      deletableIdsOnPage.every((id) => selectedIds.has(id))
                    )
                  }
                  onChange={toggleSelectAllOnPage}
                  className="h-4 w-4"
                  title="Select all deletable on this page"
                />
              </th>

              <th className={headerCell}>URD No {sortIcon("urdNo")}</th>
              <th className={headerCell}>Date {sortIcon("urdDate")}</th>
              <th className={headerCell}>
                Customer {sortIcon("customerName")}
              </th>
              <th className={headerCell}>Branch {sortIcon("branchName")}</th>
              <th className={headerCell}>Items</th>
              <th className={headerCell}>Qty</th>
              <th className={headerCell}>Net Wt</th>
              <th className={headerCell}>Amount ₹ {sortIcon("amount")}</th>
              <th className={headerCell}>Paid ₹</th>
              <th className={headerCell}>
                Outstanding ₹ {sortIcon("outstanding")}
              </th>
              <th className={headerCell}>Settlement</th>
              {/* Action col only when no selection */}
              {showActions && (
                <th className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
              )}
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {isFetching && rows.length ? (
                <SkeletonRows cols={columnCount} rows={8} />
              ) : filteredSortedRows.length > 0 ? (
                filteredSortedRows.map((doc, index) => {
                  const ids = {
                    id: doc?._id,
                    customer: doc?.customerId,
                    branch: doc?.branchId,
                  };
                  const deletable = isDeletable(doc);
                  const checked = selectedIds.has(String(ids.id));

                  const totals = doc?.totals || {};
                  const customerName =
                    ids.customer?.customerName ||
                    ids.customer?.customerId ||
                    "—";
                  const branchName = ids.branch?.branchName || "—";

                  return (
                    <motion.tr
                      key={ids.id || `${doc.urdNo}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="group border-t transition hover:bg-[#f4f0ff]"
                    >
                      {/* row checkbox */}
                      <td className="px-1 sm:px-2 py-1.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={checked}
                          disabled={!deletable}
                          title={
                            !deletable
                              ? "This entry is protected from deletion"
                              : "Select"
                          }
                          onChange={() => toggleSelect(ids.id)}
                        />
                      </td>

                      <td className={dataCell}>#{doc.urdNo || "—"}</td>
                      <td className={dataCell}>{fmtDate(doc.urdDate)}</td>
                      <td className={dataCell}>
                        <div className="flex flex-col">
                          <span className="font-medium">{customerName}</span>
                          {ids.customer?.contact ? (
                            <span className="text-[9px] sm:text-[10px] text-slate-500">
                              {ids.customer.contact}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className={dataCell}>{branchName}</td>

                      <td className={dataCell}>{totals.items ?? 0}</td>
                      <td className={dataCell}>{totals.qty ?? 0}</td>
                      <td className={dataCell}>
                        {Number(totals.netWt ?? 0).toFixed(3)}
                      </td>
                      <td className={dataCell}>₹ {money(totals.amount)}</td>
                      <td className={dataCell}>₹ {money(totals.paid)}</td>
                      <td className={dataCell}>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            Number(totals.outstanding) > 0
                              ? "bg-amber-100 text-amber-800"
                              : Number(totals.outstanding) < 0
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-slate-100 text-slate-700"
                          }`}
                          title={
                            Number(totals.outstanding) < 0
                              ? "Overpaid"
                              : "Balance due"
                          }
                        >
                          ₹ {money(Math.abs(totals.outstanding || 0))}
                          {Number(totals.outstanding) < 0 ? " (over)" : ""}
                        </span>
                      </td>

                      {/* Settlement column */}
                      <td className={dataCell}>
                        {doc.settlement ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-700">
                            {toTitle(String(doc.settlement))}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* action buttons (only when no selection) */}
                      {showActions && (
                        <td
                          className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                        >
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-violet-100 text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Print / View URD"
                            onClick={() => openReceipt(ids.id)}
                          >
                            <FaPrint />
                          </motion.button>

                          {/* <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center justify-center text-[#6b59c6] text-[11px] p-1.5 rounded-full hover:bg-[#6b59c6]/15"
                            title="Edit"
                            onClick={() =>
                              navigate(
                                `/purchase/urd-gst-list/update-urd/${ids.id}`,
                              )
                            }
                          >
                            <FaPencilAlt />
                          </motion.button> */}

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Delete"
                            disabled={deletingOne || !deletable}
                            onClick={() => {
                              if (canDelete(user)) {
                                openConfirmSingle(doc);
                              } else {
                                toast.error(
                                  "You don't have permission to delete",
                                );
                              }
                            }}
                          >
                            <FaTrashAlt />
                          </motion.button>
                        </td>
                      )}
                    </motion.tr>
                  );
                })
              ) : (
                !isLoading &&
                !isFetching && (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td
                      colSpan={columnCount}
                      className="px-4 py-8 text-center text-xs text-slate-500"
                    >
                      No matching URD records found. Try changing filters or
                      search text.
                    </td>
                  </motion.tr>
                )
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Footer – pagination (same style as LabelledStockList) */}
      <div className="shrink-0 border-t px-2 py-1.5 sm:px-4 sm:py-2 bg-slate-50 rounded-b-2xl flex items-center justify-between gap-2 sm:gap-3 text-[10px] sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {filteredSortedRows.length ? (page - 1) * limit + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {filteredSortedRows.length
                ? (page - 1) * limit + filteredSortedRows.length
                : 0}
            </span>{" "}
            of <span className="font-semibold">{totalDocs}</span> entries
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={!hasPrev || isFetching || isLoading}
              onClick={() => hasPrev && setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{
                    value: totalPages ? page : 1,
                    label: String(totalPages ? page : 1),
                  }}
                  options={Array.from(
                    { length: Math.max(1, totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching || isLoading}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {Math.max(1, totalPages)}</span>
              </span>
              <span className="hidden items-center gap-1 sm:flex">
                <span>Page</span>
                <Select
                  value={{ value: totalPages ? page : 1, label: String(totalPages ? page : 1) }}
                  options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching || isLoading}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[58px]"
                  aria-label="Select page number"
                />
                <span>of {Math.max(1, totalPages)}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={!hasNext || isFetching || isLoading}
              onClick={() =>
                hasNext &&
                setPage((p) =>
                  totalPages ? Math.min(totalPages, p + 1) : p + 1,
                )
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter modal – same pattern as LabelledStockList */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter URD entries by branch, customer and date range.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-4 text-[11px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Branch */}
                  <div>
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      styles={selectStyles}
                      value={branchValue}
                      onChange={onBranchChange}
                      options={branchOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Customer (with typeahead) */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Customer
                    </label>
                    <Select
                      ref={customerSelectRef}
                      autoFocus
                      styles={selectStyles}
                      value={customerValue}
                      onChange={onCustomerChange}
                      options={customerOptions}
                      isClearable
                      onInputChange={(input, meta) => {
                        if (meta.action === "input-change") {
                          setCustomerSearch(input.trim());
                        }
                      }}
                      isLoading={loadingCustomers}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">
                      From date
                    </label>
                    <input
                      type="date"
                      name="from"
                      value={draftFilters.from}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">To date</label>
                    <input
                      type="date"
                      name="to"
                      value={draftFilters.to}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetDraftFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="px-3 py-1.5 rounded-md bg-[#6b59c6] text-white text-[11px] font-semibold hover:bg-[#5b4ab3]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ Summary Modal (like SalesList) */}
      <AnimatePresence>
        {isSummaryOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/45 flex items-center justify-center p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSummaryOpen(false)}
          >
            <motion.div
              className="w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden bg-white"
              initial={{ scale: 0.92, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 18, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-4 sm:px-6 py-4 text-white bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce]">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/30 blur-2xl" />
                  <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-white/30 blur-2xl" />
                </div>

                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm sm:text-base font-semibold tracking-tight">
                      URD Totals Summary
                    </div>
                    <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95 leading-4">
                      <span className="font-semibold text-white">All</span>{" "}
                      (filters wise) <span className="opacity-70">•</span>{" "}
                      <span className="font-semibold text-white/90">Page</span>{" "}
                      (current)
                    </div>
                  </div>

                  <button
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition"
                    onClick={() => setIsSummaryOpen(false)}
                    title="Close"
                  >
                    <FaTimesCircle />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-3 sm:p-5">
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {summaryCards.map((c) => (
                    <div
                      key={c.key}
                      className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-2.5 sm:px-3 py-2.5 sm:py-3 shadow-sm"
                    >
                      <div className="text-[10px] sm:text-[11px] font-semibold text-slate-600 truncate">
                        {c.label}
                      </div>

                      <div className="mt-1 grid grid-cols-2 gap-2 items-end">
                        <div>
                          <div className="text-[9px] sm:text-[10px] text-slate-400">
                            All
                          </div>
                          <div className="text-[12px] sm:text-sm font-extrabold text-slate-900 leading-4">
                            {c.allVal}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-[9px] sm:text-[10px] text-slate-400">
                            Page
                          </div>
                          <div className="text-[12px] sm:text-sm font-extrabold text-[#6b59c6] leading-4">
                            {c.pageVal}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] sm:text-[11px] text-slate-500">
                    Tip: ESC to close
                  </div>

                  <button
                    onClick={() => setIsSummaryOpen(false)}
                    className="px-3 sm:px-4 py-1.5 rounded-full bg-[#6b59c6] text-white text-[11px] font-semibold hover:opacity-90 transition"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm dialog – same style as LabelledStockList */}
      <ConfirmDialog
        open={confirm.open}
        title={
          confirm.mode === "bulk" ? "Delete selected URDs?" : "Delete this URD?"
        }
        description={
          confirm.mode === "bulk"
            ? `This will delete ${confirm.payload?.count || 0} selected URD entries.`
            : `Are you sure you want to delete ${confirm.payload?.label || "this URD"}?`
        }
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        loading={deletingOne || deletingMany}
      />

      <InvoicePrintPreviewModal
        open={urdPreview.open}
        html={urdPreview.html}
        loading={urdPreview.loading}
        onClose={closeUrdPreview}
      />
    </div>
  );
};

export default URDList;